import type { LicenseQuestionAnswerType } from "@/hera-api/client/types.gen";

import { isNacStyleAnswerType } from "@/lib/nac-answer-types";

export type ContactRequirementValues = {
  contact_name_required: boolean;
  contact_address_required: boolean;
  contact_email_required: boolean;
  contact_phone_required: boolean;
  contact_company_name_required: boolean;
  contact_require_company_or_person_name: boolean;
};

export const ZERO_CONTACT_REQUIREMENTS: ContactRequirementValues = {
  contact_name_required: false,
  contact_address_required: false,
  contact_email_required: false,
  contact_phone_required: false,
  contact_company_name_required: false,
  contact_require_company_or_person_name: false,
};

/** Defaults when switching to a NAC-style answer type (uses current "answer required" as template). */
export function contactRequirementsForNewNacType(
  answerType: LicenseQuestionAnswerType,
  answerRequired: boolean,
): ContactRequirementValues {
  if (answerType === "company_contact") {
    return {
      contact_name_required: answerRequired,
      contact_address_required: answerRequired,
      contact_email_required: answerRequired,
      contact_phone_required: answerRequired,
      contact_company_name_required: answerRequired,
      contact_require_company_or_person_name: false,
    };
  }
  if (answerType === "name_address_contact") {
    return {
      contact_name_required: answerRequired,
      contact_address_required: answerRequired,
      contact_email_required: answerRequired,
      contact_phone_required: answerRequired,
      contact_company_name_required: false,
      contact_require_company_or_person_name: false,
    };
  }
  return { ...ZERO_CONTACT_REQUIREMENTS };
}

export function contactRequirementsFromApiQuestion(
  question: {
    answer_type?: LicenseQuestionAnswerType | string;
    answer_required?: boolean;
    contact_name_required?: boolean;
    contact_address_required?: boolean;
    contact_email_required?: boolean;
    contact_phone_required?: boolean;
    contact_company_name_required?: boolean;
    contact_require_company_or_person_name?: boolean;
  } | null,
): ContactRequirementValues {
  if (!question || !isNacStyleAnswerType(question.answer_type ?? "")) {
    return { ...ZERO_CONTACT_REQUIREMENTS };
  }
  const ar = question.answer_required ?? true;
  return {
    contact_name_required: question.contact_name_required ?? ar,
    contact_address_required: question.contact_address_required ?? ar,
    contact_email_required: question.contact_email_required ?? ar,
    contact_phone_required: question.contact_phone_required ?? ar,
    contact_company_name_required:
      question.answer_type === "company_contact"
        ? (question.contact_company_name_required ?? ar)
        : false,
    contact_require_company_or_person_name:
      question.answer_type === "company_contact"
        ? Boolean(question.contact_require_company_or_person_name)
        : false,
  };
}

/**
 * Subfield flags for validating NAC answers. If the API omits all `contact_*`
 * keys, falls back to legacy `answer_required` for every subfield. If any
 * `contact_*` key is present, `answer_required` is ignored for validation.
 */
export function nacFieldRequirementsForValidation(question: {
  answer_type?: string;
  answer_required?: boolean;
  contact_name_required?: boolean;
  contact_address_required?: boolean;
  contact_email_required?: boolean;
  contact_phone_required?: boolean;
  contact_company_name_required?: boolean;
  contact_require_company_or_person_name?: boolean;
}): ContactRequirementValues {
  const anyDefined =
    question.contact_name_required !== undefined ||
    question.contact_address_required !== undefined ||
    question.contact_email_required !== undefined ||
    question.contact_phone_required !== undefined ||
    question.contact_company_name_required !== undefined ||
    question.contact_require_company_or_person_name !== undefined;
  if (!anyDefined) {
    const ar = question.answer_required ?? true;
    const isCo = question.answer_type === "company_contact";
    return {
      contact_name_required: ar,
      contact_address_required: ar,
      contact_email_required: ar,
      contact_phone_required: ar,
      contact_company_name_required: isCo && ar,
      contact_require_company_or_person_name: false,
    };
  }
  return {
    contact_name_required: Boolean(question.contact_name_required),
    contact_address_required: Boolean(question.contact_address_required),
    contact_email_required: Boolean(question.contact_email_required),
    contact_phone_required: Boolean(question.contact_phone_required),
    contact_company_name_required: Boolean(
      question.contact_company_name_required,
    ),
    contact_require_company_or_person_name: Boolean(
      question.contact_require_company_or_person_name,
    ),
  };
}

export function deriveAnswerRequiredForContactQuestion(
  answerType: LicenseQuestionAnswerType,
  v: ContactRequirementValues,
): boolean {
  const base =
    v.contact_name_required ||
    v.contact_address_required ||
    v.contact_email_required ||
    v.contact_phone_required;
  if (answerType === "company_contact") {
    return (
      base ||
      v.contact_company_name_required ||
      v.contact_require_company_or_person_name
    );
  }
  return base;
}
